import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppShell } from "@/components/bi/AppShell";
import { Loading } from "@/components/bi/Loading";
import { EmptyState } from "@/components/bi/EmptyState";
import { StatusBadge } from "@/components/bi/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import { LayoutDashboard, Star, StarOff, ArrowRight, FolderKanban, Settings, Sparkles, Share2, Image as ImageIcon, X } from "lucide-react";

function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const m = Math.round(Math.max(0, Date.now() - t) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString();
}

function DashCard({ d, featured, onOpen }) {
  return (
    <div data-testid={featured ? `site-featured-card-${d.id}` : `site-recent-card-${d.id}`}
      onClick={() => onOpen(d.id)}
      className={"glass-card p-5 cursor-pointer hover:shadow-[0_22px_70px_rgba(15,23,42,0.14)] transition-shadow group " + (featured ? "border border-[color:var(--brand-mint)]/40" : "")}>
      <div className="flex items-start justify-between">
        <div className="w-11 h-11 rounded-xl hero-gradient flex items-center justify-center border border-[color:var(--glass-border)]">
          <LayoutDashboard className="w-5 h-5 text-[color:var(--brand-ocean)]" />
        </div>
        <div className="flex items-center gap-1.5">
          {featured && <span data-testid="site-featured-badge" className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[color:var(--brand-mint)] text-white font-semibold"><Star className="w-3 h-3" />Featured</span>}
          <StatusBadge status={d.status} />
        </div>
      </div>
      <h3 className={"font-display text-[color:var(--ink)] mt-4 truncate " + (featured ? "text-xl" : "text-lg")}>{d.name}</h3>
      <p className="text-sm text-[color:var(--ink-3)] mt-1 line-clamp-2 min-h-[40px]">{d.description || "No description"}</p>
      <div className="flex items-center justify-between mt-4 text-xs text-[color:var(--ink-3)]">
        <span className="flex items-center gap-1"><LayoutDashboard className="w-3.5 h-3.5" />{d.widget_count} widgets</span>
        <span className="flex items-center gap-2">
          {d.is_public && <span className="flex items-center gap-1 text-[color:var(--brand-mint)]"><Share2 className="w-3.5 h-3.5" />Shared</span>}
          <span>{relTime(d.updated_at)}</span>
        </span>
      </div>
    </div>
  );
}

export default function SiteHome() {
  const { siteId } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/sites/${siteId}/home`);
      setState(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load site");
      setState({ site: null, featured: [], recent: [], can_manage: false });
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [siteId]);

  if (state === null) return <AppShell title="Site"><Loading label="Loading site" /></AppShell>;
  const { site, featured, recent, can_manage } = state;
  if (!site) return <AppShell title="Site"><EmptyState testid="site-not-found" icon={FolderKanban} title="Site not found" description="This workspace was moved or deleted." /></AppShell>;

  const openBuilder = (id) => nav(`/builder/${id}`);
  const openDashboardsList = () => nav(`/?site=${siteId}`);

  const actions = (
    <div className="flex items-center gap-2">
      <Button data-testid="site-view-all-btn" variant="outline" onClick={openDashboardsList} className="bg-[color:var(--surface)]">
        View all dashboards <ArrowRight className="w-4 h-4 ml-1" />
      </Button>
      {can_manage && (
        <Button data-testid="site-manage-btn" variant="outline" onClick={() => setManageOpen(true)} className="bg-[color:var(--surface)]">
          <Settings className="w-4 h-4 mr-1" />Customize home
        </Button>
      )}
    </div>
  );

  return (
    <AppShell title={site.name} actions={actions}>
      <ManageHomeDialog open={manageOpen} onOpenChange={setManageOpen}
        site={site} featured={featured} recent={recent}
        onSaved={() => { setManageOpen(false); load(); }} />
      <div className="px-5 md:px-8 py-6 max-w-6xl mx-auto space-y-6">
        {/* Hero */}
        <Card className="glass-card border-0 overflow-hidden" data-testid="site-home-hero">
          {site.cover_image_url && (
            <div data-testid="site-home-cover" className="w-full h-40 md:h-52 relative overflow-hidden bg-[color:var(--surface-2)]">
              <img src={site.cover_image_url} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/10 to-transparent pointer-events-none" />
            </div>
          )}
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className={"w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 " + (site.cover_image_url ? "bg-white/95 border border-[color:var(--glass-border)] -mt-14 shadow-lg" : "bg-[color:var(--brand-ocean)]")}>
                <FolderKanban className={"w-7 h-7 " + (site.cover_image_url ? "text-[color:var(--brand-ocean)]" : "text-white")} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 data-testid="site-home-name" className="font-display text-2xl text-[color:var(--ink)] truncate">{site.name}</h1>
                  {site.is_default && <span className="text-[10px] uppercase font-bold text-[color:var(--brand-ocean)] px-2 py-0.5 rounded-full border border-[color:var(--brand-ocean)]/30">default site</span>}
                </div>
                {site.description && <p className="text-sm text-[color:var(--ink-3)] mt-1">{site.description}</p>}
                {site.home_intro && (
                  <div data-testid="site-home-intro" className="mt-4 text-sm text-[color:var(--ink-2)] leading-relaxed whitespace-pre-wrap max-w-2xl">
                    {site.home_intro}
                  </div>
                )}
                <div className="mt-4 flex items-center gap-4 text-xs text-[color:var(--ink-3)]">
                  <span className="flex items-center gap-1.5"><LayoutDashboard className="w-3.5 h-3.5" />{site.dashboard_count || 0} dashboards</span>
                  <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" />{featured.length} featured</span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Featured */}
        {featured.length > 0 && (
          <section data-testid="site-featured-section">
            <div className="flex items-baseline justify-between mb-3">
              <h2 className="font-display text-lg text-[color:var(--ink)] flex items-center gap-2"><Star className="w-4 h-4 text-[color:var(--brand-mint)]" />Featured</h2>
              <span className="text-xs text-[color:var(--ink-3)]">Team essentials for {site.name}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {featured.map((d) => <DashCard key={d.id} d={d} featured onOpen={openBuilder} />)}
            </div>
          </section>
        )}

        {/* Recent */}
        <section data-testid="site-recent-section">
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-lg text-[color:var(--ink)]">Recently updated</h2>
            <span className="text-xs text-[color:var(--ink-3)]">{recent.length} shown</span>
          </div>
          {recent.length === 0 && featured.length === 0 ? (
            <EmptyState testid="site-empty" icon={LayoutDashboard} title="No dashboards in this site yet"
              description="Create a dashboard, then move it into this site from the dashboards list." />
          ) : recent.length === 0 ? (
            <p className="text-sm text-[color:var(--ink-3)] italic">Everything in {site.name} is already featured above.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recent.map((d) => <DashCard key={d.id} d={d} onOpen={openBuilder} />)}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function ManageHomeDialog({ open, onOpenChange, site, featured, recent, onSaved }) {
  const [intro, setIntro] = useState("");
  const [selected, setSelected] = useState([]);
  const [cover, setCover] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const allDashes = useMemo(() => {
    const seen = new Set();
    return [...featured, ...recent].filter((d) => { if (seen.has(d.id)) return false; seen.add(d.id); return true; });
  }, [featured, recent]);

  useEffect(() => {
    if (open) {
      setIntro(site?.home_intro || "");
      setSelected(featured.map((f) => f.id));
      setCover(site?.cover_image_url || "");
    }
  }, [open, site, featured]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 8 ? s : [...s, id])));

  const uploadCover = async (file) => {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("Please pick an image file"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("Image must be under 10 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCover(data.url);
      toast.success("Cover uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/sites/${site.id}`, { home_intro: intro, featured_dashboard_ids: selected, cover_image_url: cover || "" });
      toast.success("Site home updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Settings className="w-4 h-4" />Customize home for {site?.name}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-[color:var(--ink-2)]">Cover banner</label>
            <div className="mt-1.5 rounded-lg border border-[color:var(--glass-border)] overflow-hidden bg-[color:var(--surface-2)]">
              {cover ? (
                <div className="relative">
                  <img data-testid="site-cover-preview" src={cover} alt="cover" className="w-full h-24 object-cover" />
                  <button data-testid="site-cover-clear" onClick={() => setCover("")}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 hover:bg-white text-[color:var(--ink-2)] flex items-center justify-center shadow">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <label className="flex items-center justify-center gap-2 h-24 cursor-pointer text-xs text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]">
                  <ImageIcon className="w-4 h-4" />{uploading ? "Uploading…" : "Click to upload a banner image"}
                  <input data-testid="site-cover-input" type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadCover(e.target.files && e.target.files[0])} />
                </label>
              )}
            </div>
            <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Recommended: 1600×400px, under 2 MB. Rendered at the top of the site home.</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-[color:var(--ink-2)]">Welcome message</label>
            <Textarea data-testid="site-home-intro-input" value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder="A short introduction for teams that land on this site." className="mt-1.5" rows={4} />
            <p className="text-[10px] text-[color:var(--ink-3)] mt-1">{intro.length}/800 characters — shown at the top of the site home.</p>
          </div>
          <div>
            <div className="flex items-baseline justify-between">
              <label className="text-xs font-semibold text-[color:var(--ink-2)]">Featured dashboards</label>
              <span className="text-[10px] text-[color:var(--ink-3)]">{selected.length}/8</span>
            </div>
            <div className="mt-1.5 max-h-56 overflow-y-auto space-y-1 rounded-lg border border-[color:var(--glass-border)] p-2">
              {allDashes.length === 0 && <div className="text-xs text-[color:var(--ink-3)] text-center py-4">No dashboards in this site yet</div>}
              {allDashes.map((d) => {
                const active = selected.includes(d.id);
                return (
                  <label key={d.id} data-testid={`site-featured-toggle-${d.id}`} className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-[color:var(--surface-2)] cursor-pointer">
                    <Checkbox checked={active} onCheckedChange={() => toggle(d.id)} />
                    <span className="text-sm text-[color:var(--ink-2)] truncate flex-1">{d.name}</span>
                    {active ? <Star className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" /> : <StarOff className="w-3.5 h-3.5 text-[color:var(--ink-3)]" />}
                  </label>
                );
              })}
            </div>
            <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Pick up to 8 dashboards. They render as large hero cards on the site home.</p>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="site-home-save-btn" onClick={save} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white">Save site home</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
